import React, { useState } from 'react';
import { ChevronDown, GraduationCap, Building2, FileText, Loader, Check, AlertTriangle } from 'lucide-react';
import DetailPanel from './DetailPanel';
import { fmtMoney, fmtNum } from './format';
import { formatPlayTime } from '../../../utils/format';
import generatePlayerProfilePDF from '../../../utils/generatePlayerProfilePDF';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert a Blob to a base64 string (sans the data:…;base64, prefix).
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      // result === "data:application/pdf;base64,JVBERi0xLj…"
      const idx = result.indexOf('base64,');
      resolve(idx >= 0 ? result.slice(idx + 7) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * SupervisionRow — single apprentice row in the global table, with
 * expandable detail panel below. Extracted 29/05/2026.
 *
 * 04/06/2026 — added "📄 PDF" CTA per row (pedagogical profile sheet).
 * The PDF is generated client-side, downloaded for the supervisor AND
 * sent to the backend so the player receives:
 *   - an in-game message (via /telemetry/pending-messages poll)
 *   - an email with the PDF attached (Resend / Gmail fallback)
 */
export default function SupervisionRow({ item, expanded, onToggle, token }) {
  const lic = item.license || {};
  const s = item.snapshot;
  const alert = s && ((s.equity || 0) < 0 || (s.treasury || 0) < 5000);

  // Idle | sending | ok-both | ok-game-only | error
  const [pdfState, setPdfState] = useState('idle');

  const handleExportPDF = async (e) => {
    e.stopPropagation();
    if (!s) return;
    setPdfState('sending');
    try {
      // Generate the PDF locally and grab the Blob (no auto-download yet).
      const blob = generatePlayerProfilePDF({ snapshot: s, license: lic, download: false });
      // Build a friendly download filename for the supervisor copy.
      const slug = (lic.holder_name || s.company_name || 'apprenant')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      const filename = `hst-fiche-${slug}.pdf`;

      // Convert to base64 for the backend payload.
      const pdfBase64 = await blobToBase64(blob);

      // POST to /api/admin/player-message — backend handles inbox + email.
      let backendOk = false;
      let emailSent = false;
      try {
        const res = await fetch(`${API}/api/admin/player-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            license_key: lic.license_key,
            subject: 'Fiche apprenant — votre profil de pilotage',
            body:
              "Votre responsable pédagogique vient de générer votre fiche apprenant. "
              + "Vous recevez ce message in-game ainsi qu'une copie par email avec le PDF en pièce jointe. "
              + "Prenez le temps de le consulter pour identifier vos points forts et vos axes d'amélioration.",
            sender_name: 'Responsable pédagogique',
            kind: 'info',
            send_email: true,
            pdf_base64: pdfBase64,
            pdf_filename: filename,
          }),
        });
        if (res.ok) {
          const json = await res.json();
          backendOk = !!json.ok;
          emailSent = !!json.email_sent;
        }
      } catch (err) {
        backendOk = false;
      }

      // Download the supervisor's copy regardless of backend success.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (backendOk && emailSent) setPdfState('ok-both');
      else if (backendOk) setPdfState('ok-game-only');
      else setPdfState('error');
    } catch (err) {
      console.error('[hst] PDF export failed:', err);
      setPdfState('error');
    }
    // Auto-reset after 5s so the supervisor can re-export later.
    setTimeout(() => setPdfState('idle'), 5000);
  };

  const pdfBtnContent = (() => {
    switch (pdfState) {
      case 'sending':
        return (<><Loader className="w-3 h-3 animate-spin" /> Envoi…</>);
      case 'ok-both':
        return (<><Check className="w-3 h-3" /> Envoyé + Email</>);
      case 'ok-game-only':
        return (<><Check className="w-3 h-3" /> Message envoyé</>);
      case 'error':
        return (<><AlertTriangle className="w-3 h-3" /> Erreur</>);
      default:
        return (<><FileText className="w-3 h-3" /> PDF</>);
    }
  })();
  const pdfBtnClass = (() => {
    switch (pdfState) {
      case 'sending':       return 'bg-slate-600/15 text-slate-300 border-slate-500/30 cursor-wait';
      case 'ok-both':       return 'bg-emerald-600/15 text-emerald-300 border-emerald-500/40';
      case 'ok-game-only':  return 'bg-amber-600/15 text-amber-300 border-amber-500/40';
      case 'error':         return 'bg-rose-600/15 text-rose-300 border-rose-500/40';
      default:              return 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-600/25';
    }
  })();
  const pdfBtnTitle = (() => {
    switch (pdfState) {
      case 'ok-both':       return "Message envoyé au joueur et email envoyé avec la pièce jointe.";
      case 'ok-game-only':  return "Message envoyé au joueur mais l'email n'a pas pu partir (email manquant ou échec d'envoi). Le PDF a été téléchargé pour vous.";
      case 'error':         return "Échec de l'envoi au joueur. Le PDF a été téléchargé pour vous.";
      default:              return "Générer la fiche PDF, l'envoyer au joueur (in-game + email) et la télécharger en local.";
    }
  })();

  return (
    <React.Fragment>
      <tr
        className={`border-b border-ink-600 hover:bg-ink-700/40 transition-colors cursor-pointer ${alert ? 'bg-warn/5' : ''}`}
        onClick={onToggle}
        data-testid={`global-row-${lic.license_key}`}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-300 font-bold text-xs">
                {(lic.holder_name || '?').slice(0, 1).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-medium text-slate-100 truncate">{lic.holder_name || '—'}</p>
              <p className="text-[9px] text-slate-500 font-mono">{lic.license_key}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-2.5">
          {item.cohort_name ? (
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 w-fit">
                <GraduationCap className="w-2.5 h-2.5" /> {item.cohort_name}
              </span>
              {item.establishment && (
                <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded text-emerald-300 w-fit">
                  <Building2 className="w-2 h-2" /> {item.establishment}
                </span>
              )}
            </div>
          ) : (
            <span className="text-[10px] text-slate-600 italic">Indépendant</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-slate-300 text-xs">
          {lic.bankruptcy ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40 font-bold"
              data-testid={`bankruptcy-badge-${lic.license_key}`}
            >
              ⚖ Liquidé J{lic.bankruptcy.liquidation_day}
            </span>
          ) : (
            s?.company_name || <span className="text-slate-600 italic">Pas encore joué</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-right text-slate-200 font-mono text-xs">
          {s ? `J${s.total_days}` : '—'}
        </td>
        <td className="px-4 py-2.5 text-right text-sky-300 font-mono text-xs" data-testid={`playtime-${lic.license_key}`}>
          {s ? formatPlayTime(s.play_time_seconds) : '—'}
        </td>
        <td className={`px-4 py-2.5 text-right font-mono text-xs ${s && s.treasury < 5000 ? 'text-warn font-bold' : 'text-slate-200'}`}>
          {s ? fmtMoney(s.treasury) : '—'}
        </td>
        <td className={`px-4 py-2.5 text-right font-mono text-xs ${s && s.equity < 0 ? 'text-danger font-bold' : 'text-slate-200'}`}>
          {s ? fmtMoney(s.equity) : '—'}
        </td>
        <td className="px-4 py-2.5 text-right text-slate-200 font-mono text-xs">{s ? fmtNum(s.fleet_total) : '—'}</td>
        <td className="px-4 py-2.5 text-right text-slate-200 font-mono text-xs">{s ? fmtNum(s.reputation) : '—'}</td>
        <td className="px-4 py-2.5 text-right">
          <div className="flex items-center justify-end gap-2">
            {s && (
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={pdfState === 'sending'}
                title={pdfBtnTitle}
                className={`text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border transition-colors ${pdfBtnClass}`}
                data-testid={`pdf-export-${lic.license_key}`}
              >
                {pdfBtnContent}
              </button>
            )}
            <ChevronDown className={`w-3.5 h-3.5 inline transition-transform text-slate-500 ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-ink-600 bg-ink-700/30">
          <td colSpan={10} className="px-4 py-3">
            <DetailPanel item={item} />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}
